import React, { useState, useContext, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import _ from "lodash";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import eligibility from "../../../../gql/eligibility";
import BeneficiarySearchNSelect from "../requestEligibility/BeneficiarySearchNSelect";
import { appContext } from "../../../../context/ContextProvider";
import "../../styles/eligibility.css";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AppButton from "../../../../shared/AppButton";
import CustomFilterInput from "../../../../shared/CustomFilterInput";

function RequestEligibilityDialog({
  errorData,
  setErrorData,
  refetch,
  fromDate,
  toDate,
  onClose,
}) {
  const { t, i18n } = useTranslation();
  const currentDate = new Date();
  const { setShowErrorAlert } = useContext(appContext);
  const navigate = useNavigate();

  const [showLoader, setShowLoader] = useState(false);
  const [insurancePlans, setInsurancePlans] = useState([]);
  const [payers, setPayers] = useState([]);

  const formatInsurancePlansData = (data) => {
    let returnData = [];
    if (data?.length > 0) {
      data.map((item) => {
        returnData.push({
          id: item.id,
          memberCardId: item.member_card_id,
          policyNumber: item.policy_number,
          expiryDate: item.expiry_date,
          isPrimary: item.is_primary,
          payerId: item.payer_id,
          relationWithSubscriber: item.relation_with_subscriber,
          coverageType: item.coverage_type,
          patientShare: item.patient_share,
          maxLimit: item.max_limit,
          patientId: item.patient_id,
          name: item?.payer?.name_en,
          license_id: item?.payer?.license_id,
        });
      });
    }
    return returnData;
  };

  React.useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));
    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  const [checkEligibility, { loading }] = useMutation(
    eligibility.ELIGIBILITY_CHECK,
    {
      update(proxy, result) {
        if (result.data.verificationRequest.nphies_status === "error") {
          result.data.verificationRequest.nphies_err.map((err) => {
            toast.error(err.display);
          });
          setShowLoader(false);
        } else {
          toast.success("Data send to payer successfully!");
          onSuccessfulRequestResponse(result.data);
        }
      },
      onError(error) {
        onResponseError(error.graphQLErrors[0]);
      },
    },
  );

  const onSuccessfulRequestResponse = (data) => {
    if (!data.verificationRequest.nphies_err) {
      setShowLoader(false);
      refetch({
        limit: 10,
        page: 1,
        fromDate: fromDate,
        toDate: toDate,
      });
      if (onClose) onClose();
    } else {
      setShowLoader(false);
      setErrorData(data.verificationRequest.nphies_err);
      setShowErrorAlert(true);
    }
  };
  const onResponseError = (error) => {
    setShowLoader(false);
    refetch({
      limit: 10,
      page: 1,
      fromDate: fromDate,
      toDate: toDate,
    });
    toast.error(`${error?.message}`);
  };

  const formatInputData = (postData) => {
    return {
      start_date: postData.startDate,
      end_date: postData.endDate,
      payer_id: `${postData.payerId}`,
      patient_id: postData.patientId,
      insurance_plan_id: `${postData.insurancePlan.id}`,
      purpose: postData.purpose,
      is_new_born: postData.isNewBorn,
      is_referral: postData.isReferral,
    };
  };

  const addData = (values) => {
    let data = _.cloneDeep(values);
    setShowLoader(true);
    checkEligibility({ variables: { input: formatInputData(data) } });
  };

  const setPurposeValues = (valChecked, currentVal, purposeArray) => {
    if (valChecked) {
      if (!purposeArray.includes(currentVal)) {
        purposeArray.push(currentVal);
      }
    } else {
      const index = purposeArray.indexOf(currentVal);
      if (index !== -1) {
        purposeArray.splice(index, 1);
      }
    }
    return purposeArray;
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    validateForm,
  } = useFormik({
    initialValues: {
      isNewBorn: false,
      isReferral: false,
      patientId: "",
      subscriber: "",
      benefeciaryName: "",
      startDate: currentDate.toISOString().substring(0, 10),
      endDate: currentDate.toISOString().substring(0, 10),
      purpose: ["benefits", "validation"],
      benefits: true,
      validations: true,
      discovery: false,
      insurancePlan: "",
      insurancePlanIndex: "",
      payerId: "",
    },
    validationSchema: () => {
      return Yup.object().shape({
        patientId: Yup.string().required("Patient ID is required"),
        payerId: values.discovery
          ? Yup.string().required("Payer ID is required")
          : Yup.string(),
        insurancePlan: values.discovery
          ? Yup.mixed()
          : Yup.mixed().required("Insurance is required"),
      });
    },
    onSubmit: (values) => {
      addData(values);
    },
  });

  return (
    <React.Fragment>
      <div className="p-1">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-1.5 gap-y-1">
          <div>
            <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
              {t("Options")}
            </label>

            <div className="flex justify-between items-center bg-slate-100/50 border !border-gray-300 rounded-[6px] px-2 !h-[28px] !py-0  gap-x-6">
              <span className=" text-gray-700 text-[12px] leading-tight">
                {t("Referral")}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={values.isReferral}
                  onChange={(e) =>
                    setFieldValue("isReferral", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
          <div className="flex justify-between items-center bg-slate-100/50 border !border-gray-300 rounded-[6px] px-2 !h-[28px] !py-0 mt-[20px]  gap-x-6">
            <span className="text-gray-700 text-[12px] leading-tight">
              {t("Emergency")}
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
          <div className="flex justify-between items-center bg-slate-100/50 border !border-gray-300 rounded-[6px] px-2 !h-[28px] !py-0 gap-x-6 mt-[20px]">
            <span className=" text-gray-700 text-[12px] leading-tight">
              {t("New Born?")}
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={values.isNewBorn}
                onChange={(e) => setFieldValue("isNewBorn", e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className={`${values.isNewBorn ? "col-span-1" : "col-span-2"}`}>
            <label className="block !font-[600] text-[12px] text-gray-700 mb-0.5 capitalize">
              {t("Beneficiary Name / ID")}
            </label>
            <div className="input-container w-full ">
              <BeneficiarySearchNSelect
                setFieldValue={(val) => {
                  if (values.isNewBorn) {
                  } else {
                    setInsurancePlans(
                      formatInsurancePlansData(val.insurancePlans),
                    );
                  }

                  setFieldValue("patientId", val.id);
                  setFieldValue("benefeciaryName", val.name);

                  setFieldValue(
                    "insurancePlan",
                    val?.insurancePlans && val?.insurancePlans?.length > 0
                      ? formatInsurancePlansData(val.insurancePlans)[0]
                      : "",
                  );
                  setFieldValue("insurancePlanIndex", 0);

                  setFieldValue(
                    "payerId",
                    val?.insurancePlans && val?.insurancePlans?.length > 0
                      ? formatInsurancePlansData(val.insurancePlans)[0]?.payerId
                      : "",
                  );

                  validateForm();
                }}
                name="eligibility"
              />

              {errors.patientId && touched.patientId ? (
                <p className="text-xs text-red-500 mt-0.5">
                  {errors.patientId}
                </p>
              ) : null}
            </div>
          </div>

          {values.isNewBorn && (
            <div className="col-span-1 w-full animate-fadeIn">
              <label className="block !font-[600] text-[12px] text-gray-700 mb-0.5 capitalize">
                {t("Select Subscriber")}
              </label>

              <div className="input-container w-full ">
                <BeneficiarySearchNSelect
                  setFieldValue={(val) => {
                    setInsurancePlans(
                      formatInsurancePlansData(val.insurancePlans),
                    );
                    setFieldValue("subscriber", val.id);
                    setFieldValue("insurancePlan", "");
                    setFieldValue("insurancePlanIndex", "");
                    setFieldValue("payerId", "");
                  }}
                  name="eligibility"
                />
              </div>
            </div>
          )}

          <CustomFilterInput
            label={t("Start Date")}
            name="startDate"
            value={values.startDate}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t("Start Date")}
            type="date"
          />
          <CustomFilterInput
            label={t("End Date")}
            name="endDate"
            value={values.endDate}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t("End Date")}
            type="date"
          />
          <div>
            <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
              {t("Purpose")} <span className="text-red-500">*</span>
            </label>
            {/* <div className="flex items-center"> */}
            <div className="flex justify-between items-center bg-slate-100/50 border !border-gray-300 rounded-[6px] px-2 !h-[28px] !py-0 gap-x-6">
              <label className="text-[12px] leading-tight text-gray-700">
                {t("Benefits")}
              </label>
              <input
                type="checkbox"
                name="benefits"
                checked={values.benefits}
                onChange={() => {
                  setFieldValue(
                    "purpose",
                    setPurposeValues(
                      !values.benefits,
                      "benefits",
                      values.purpose,
                    ),
                  );
                  setFieldValue("benefits", !values.benefits);
                }}
                className="h-4 w-4 !text-primary focus:ring-primary border-gray-300 rounded"
              />
            </div>
          </div>
          <div>
            <label className="block !font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
              {t("Validations")} <span className="text-red-500">*</span>
            </label>
            {/* <div className="flex items-center"> */}
            <div className="flex justify-between items-center bg-slate-100/50 border !border-gray-300 rounded-[6px] px-2 !h-[28px] !py-0 gap-x-6">
              <label className=" text-[12px] leading-tight text-gray-700">
                {t("Validations")}
              </label>
              <input
                type="checkbox"
                name="validations"
                checked={values.validations}
                onChange={() => {
                  setFieldValue(
                    "purpose",
                    setPurposeValues(
                      !values.validations,
                      "validation",
                      values.purpose,
                    ),
                  );
                  setFieldValue("validations", !values.validations);
                }}
                className="h-4 w-4 !text-primary focus:ring-primary border-gray-300 rounded"
              />
            </div>
          </div>
          <div>
            <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
              {t("Select Discovery")}
            </label>
            {/* <div className="flex items-center"> */}
            <div className="flex justify-between items-center bg-slate-100/50 border !border-gray-300 rounded-[6px] px-2 !h-[28px] !py-0 gap-x-6">
              <label
                htmlFor="discovery-check"
                className=" text-[12px] leading-tight text-gray-700"
              >
                {t("Discovery")}
              </label>
              <input
                type="checkbox"
                id="discovery-check"
                name="discovery"
                checked={values.discovery}
                onChange={() => {
                  setFieldValue(
                    "purpose",
                    setPurposeValues(
                      !values.discovery,
                      "discovery",
                      values.purpose,
                    ),
                  );
                  setFieldValue("discovery", !values.discovery);
                }}
                className="h-4 w-4 !text-primary focus:ring-primary border-gray-300 rounded"
              />
            </div>
          </div>
          <div className="col-span-2">
            <CustomFilterInput
              label={`${
                values.discovery ? t("Payers") : t("Insurance Plan")
              } *`}
              name="insurancePlanIndex"
              value={values.insurancePlanIndex}
              placeholder={t("Select One")}
              isSelect
              onChange={(e) => {
                const iPlanIndex = e.target.value;

                if (iPlanIndex !== "") {
                  setFieldValue(
                    "insurancePlan",
                    values.discovery ? iPlanIndex : insurancePlans[iPlanIndex],
                  );

                  setFieldValue(
                    "payerId",
                    values.discovery
                      ? iPlanIndex
                      : insurancePlans[iPlanIndex].payerId,
                  );
                } else {
                  setFieldValue("insurancePlan", "");
                  setFieldValue("payerId", "");
                }

                setFieldValue("insurancePlanIndex", iPlanIndex);
                validateForm();
              }}
              onBlur={handleBlur}
              renderOptions={() =>
                values.discovery
                  ? payers.map((category) => (
                      <optgroup
                        key={category.id}
                        label={
                          category?.name_en
                            ? `${category?.name_en} (${category?.license_id})`
                            : ""
                        }
                      >
                        {category?.payers?.map((insurance, idx) => (
                          <option
                            key={`${insurance?.id}-${idx}`}
                            value={insurance.id}
                          >
                            {insurance?.name_en} ({insurance?.license_id})
                          </option>
                        ))}
                      </optgroup>
                    ))
                  : insurancePlans.map((item, index) => (
                      <option key={index} value={index}>
                        {item?.name} ({item?.license_id})
                      </option>
                    ))
              }
            />

            {errors.insurancePlan && touched.insurancePlan && (
              <p className="text-xs text-red-500">{errors.insurancePlan}</p>
            )}

            {errors.payerId && touched.payerId && (
              <p className="text-xs text-red-500">{errors.payerId}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end border-t pt-2 mt-2 gap-2">
          <AppButton
            label={t("Cancel")}
            variant="reset"
            onClick={onClose}
            className="mr-2 !text-gray-500"
          />
          <AppButton
            label={t("Request Eligibility")}
            onClick={handleSubmit}
            disabled={showLoader || loading}
            startIcon={<AddIcon />}
            isLoading={showLoader || loading}
            loadingText={t("Requesting")}
          />
        </div>
      </div>
    </React.Fragment>
  );
}

export default RequestEligibilityDialog;
